import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, Eye, Phone, 
  Mail, MapPin, Loader2, Calendar
} from 'lucide-react';
import api from '../../utils/api';

// Indian States list
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

const MobileCustomers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [districts, setDistricts] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    mobile: '',
    alternate_mobile: '',
    email: '',
    address: '',
    postal_code: '',
    city: '',
    district: '',
    state: '',
    gst_number: '',
    notes: ''
  });

  // Fetch districts when state changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (formData.state) {
        try {
          const response = await api.get(`/customers/districts/${encodeURIComponent(formData.state)}`);
          setDistricts(response.data);
        } catch (error) {
          console.error('Failed to fetch districts');
          setDistricts([]);
        }
      } else {
        setDistricts([]);
      }
    };
    fetchDistricts();
  }, [formData.state]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers/');
      // Already sorted by created_at desc from backend
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleCreateCustomer = async () => {
    if (!formData.company_name || !formData.contact_person || !formData.mobile) {
      toast.error('Please fill required fields (Company Name, Contact Person, Mobile)');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        email: formData.email || 'noemail@placeholder.com',
        address: formData.address || 'N/A',
        postal_code: formData.postal_code || '000000',
        city: formData.city || 'N/A',
        district: formData.district || 'N/A',
        state: formData.state || 'Tamil Nadu',
        status: 'Active'
      };
      await api.post('/customers/', submitData);
      toast.success('Customer created successfully');
      setCreateDialogOpen(false);
      setFormData({
        company_name: '',
        contact_person: '',
        mobile: '',
        alternate_mobile: '',
        email: '',
        address: '',
        postal_code: '',
        city: '',
        district: '',
        state: '',
        gst_number: '',
        notes: ''
      });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      (customer.company_name || '').toLowerCase().includes(searchLower) ||
      (customer.contact_person || '').toLowerCase().includes(searchLower) ||
      (customer.mobile || '').includes(searchTerm) ||
      (customer.alternate_mobile || '').includes(searchTerm) ||
      (customer.district || '').toLowerCase().includes(searchLower) ||
      (customer.state || '').toLowerCase().includes(searchLower);
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Customers</h1>
        <Button onClick={() => setCreateDialogOpen(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search by name, mobile, district, state..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Customers List */}
      <div className="space-y-3">
        {filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No customers found</p>
            </CardContent>
          </Card>
        ) : (
          filteredCustomers.map((customer) => (
            <Card key={customer.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold">{customer.company_name}</p>
                    <p className="text-sm text-gray-600">{customer.contact_person}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={customer.status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-gray-50'}>
                      {customer.status || 'Active'}
                    </Badge>
                    {customer.created_at && (
                      <p className="text-xs text-gray-400 mt-1 flex items-center justify-end gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(customer.created_at).toLocaleDateString('en-IN')}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  {customer.mobile && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${customer.mobile}`} className="text-blue-600">{customer.mobile}</a>
                      {customer.alternate_mobile && (
                        <span className="text-gray-400">|</span>
                      )}
                      {customer.alternate_mobile && (
                        <a href={`tel:${customer.alternate_mobile}`} className="text-blue-600">{customer.alternate_mobile}</a>
                      )}
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {(customer.district || customer.city || customer.state) && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{[customer.city, customer.district, customer.state].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => { setSelectedCustomer(customer); setViewDialogOpen(true); }}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {customer.mobile && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      asChild
                    >
                      <a href={`tel:${customer.mobile}`}>
                        <Phone className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Customer Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Company Name *</Label>
              <Input 
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder="Company name"
              />
            </div>
            <div>
              <Label>Contact Person *</Label>
              <Input 
                value={formData.contact_person}
                onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                placeholder="Contact person name"
              />
            </div>
            <div>
              <Label>Mobile *</Label>
              <Input 
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                placeholder="Mobile number"
              />
            </div>
            <div>
              <Label>Alternate Mobile (Optional)</Label>
              <Input 
                type="tel"
                value={formData.alternate_mobile}
                onChange={(e) => setFormData({...formData, alternate_mobile: e.target.value})}
                placeholder="Alternate mobile number"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Email address"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input 
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>City</Label>
                <Input 
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="City"
                />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input 
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  placeholder="Postal code"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>State</Label>
                <Select 
                  value={formData.state} 
                  onValueChange={(value) => setFormData({...formData, state: value, district: ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {INDIAN_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>District</Label>
                <Select 
                  value={formData.district} 
                  onValueChange={(value) => setFormData({...formData, district: value})}
                  disabled={!formData.state || districts.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={formData.state ? "Select district" : "Select state first"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {districts.map(district => (
                      <SelectItem key={district} value={district}>{district}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>GST Number</Label>
              <Input 
                value={formData.gst_number}
                onChange={(e) => setFormData({...formData, gst_number: e.target.value})}
                placeholder="GST number (optional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Customer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCustomer?.company_name}</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Contact Person</p>
                  <p className="font-medium">{selectedCustomer.contact_person || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge variant="outline">{selectedCustomer.status || 'Active'}</Badge>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-medium">{selectedCustomer.mobile || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Alternate Mobile</p>
                  <p className="font-medium">{selectedCustomer.alternate_mobile || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium truncate">{selectedCustomer.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created Date</p>
                  <p className="font-medium">{selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString('en-IN') : 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Address</p>
                  <p className="font-medium">
                    {[selectedCustomer.address, selectedCustomer.city, selectedCustomer.district, selectedCustomer.state, selectedCustomer.postal_code].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
                {selectedCustomer.gst_number && (
                  <div className="col-span-2">
                    <p className="text-gray-500">GST Number</p>
                    <p className="font-medium">{selectedCustomer.gst_number}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileCustomers;
